import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
              alt="2nd Team Casting" 
              className="h-20 w-auto"
            />
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 pt-36 pb-20">
        <h1 className="font-['Playfair_Display'] text-4xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-white/40 text-sm mb-10">Last updated: September 2026</p>

        <div className="prose prose-invert max-w-none space-y-8 text-white/70 text-sm leading-relaxed">
          
          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">1. Who We Are</h2>
            <p>
              2nd Team Casting ("2nd Team", "we", "us", "our") is a stand-in and picture double casting agency operating in the United Kingdom. 
              Our registered address is Unit 121927, PO Box 6945, London, W1A 6US.
            </p>
            <p className="mt-2">
              We are the data controller for the personal information we collect through our website and platform at 2ndteam.co.uk.
            </p>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">2. What Information We Collect</h2>
            <p>We collect and process the following personal data:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
              <li><strong className="text-white/90">Identity data:</strong> Full name, gender, date of birth</li>
              <li><strong className="text-white/90">Contact data:</strong> Email address, phone number (including WhatsApp), postal address</li>
              <li><strong className="text-white/90">Physical data:</strong> Height, weight, body measurements (chest, waist, hips, neck, inside leg, shoe size), hair colour, skin tone, ethnicity</li>
              <li><strong className="text-white/90">Visual data:</strong> Headshot photographs, full body photographs, additional images uploaded to your profile</li>
              <li><strong className="text-white/90">Professional data:</strong> Previous production experience, actor names stood in for, days worked, experience level, radio phrases proficiency</li>
              <li><strong className="text-white/90">Financial data:</strong> Preferred day rate, bank details (where provided for payment), invoice records</li>
              <li><strong className="text-white/90">Documents:</strong> Right-to-work documentation, identification documents you upload</li>
              <li><strong className="text-white/90">Technical data:</strong> IP address, browser type, login timestamps</li>
              <li><strong className="text-white/90">Availability data:</strong> Calendar availability, booking dates, call times, wrap times</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">3. How We Use Your Information</h2>
            <p>We use your personal data for the following purposes:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
              <li><strong className="text-white/90">Casting and matching:</strong> To match stand-ins to production requests based on physical characteristics, availability, and experience</li>
              <li><strong className="text-white/90">Communication:</strong> To send availability checks, booking confirmations, and production notifications via email and WhatsApp</li>
              <li><strong className="text-white/90">Profile management:</strong> To create and maintain your professional comp card / CV for sharing with productions</li>
              <li><strong className="text-white/90">Invoicing and payments:</strong> To generate invoices, process payments, and maintain financial records</li>
              <li><strong className="text-white/90">Account administration:</strong> To manage your account, verify your identity, and provide customer support</li>
              <li><strong className="text-white/90">Service improvement:</strong> To improve our matching algorithms and platform functionality</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">4. Legal Basis for Processing</h2>
            <p>We process your personal data on the following legal bases under UK GDPR:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
              <li><strong className="text-white/90">Contract:</strong> Processing is necessary for the performance of our agency agreement with you</li>
              <li><strong className="text-white/90">Legitimate interests:</strong> To operate our casting agency, match stand-ins to productions, and improve our services</li>
              <li><strong className="text-white/90">Consent:</strong> Where you have given specific consent, for example when uploading photographs or agreeing to WhatsApp communications</li>
              <li><strong className="text-white/90">Legal obligation:</strong> Where we are required to process data for tax, employment, or regulatory purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">5. Who We Share Your Data With</h2>
            <p>We may share your personal data with:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
              <li><strong className="text-white/90">Production companies:</strong> Limited profile information (first name, physical characteristics, experience, photographs) is shared with Crowd 2nds and productions who request stand-ins. Full contact details are only shared once you are booked.</li>
              <li><strong className="text-white/90">Service providers:</strong> We use Google (Gmail for email communications, Sheets for data management), Meta/WhatsApp (for messaging), and secure cloud hosting for data storage.</li>
              <li><strong className="text-white/90">HMRC and accountants:</strong> Invoice and payment data as required for tax compliance.</li>
            </ul>
            <p className="mt-2">We do not sell your personal data to third parties.</p>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">6. Data Retention</h2>
            <p>
              We retain your personal data for as long as your account is active and you are on our roster. 
              If you request account deletion, we will remove your profile data within 30 days, except where we are required to retain records for legal, tax, or contractual obligations (typically 6 years for financial records).
            </p>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">7. Your Rights</h2>
            <p>Under UK GDPR, you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
              <li><strong className="text-white/90">Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong className="text-white/90">Rectification:</strong> Ask us to correct inaccurate data</li>
              <li><strong className="text-white/90">Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong className="text-white/90">Restriction:</strong> Ask us to limit how we use your data</li>
              <li><strong className="text-white/90">Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong className="text-white/90">Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong className="text-white/90">Withdraw consent:</strong> Where processing is based on consent, you can withdraw it at any time</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, please contact us at <a href="mailto:info@2ndteam.co.uk" className="text-[#D4AF37] hover:underline">info@2ndteam.co.uk</a>.
            </p>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">8. Data Security</h2>
            <p>
              We implement appropriate technical and organisational measures to protect your personal data, including encrypted data transmission (HTTPS/TLS), secure password hashing, rate-limited authentication, and access controls restricting data visibility based on user roles.
            </p>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">9. International Transfers</h2>
            <p>
              Your data may be processed by service providers located outside the UK (e.g. Google, Meta). Where this occurs, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses or adequacy decisions.
            </p>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">10. Cookies</h2>
            <p>
              Our platform uses essential cookies and local storage for authentication (JWT tokens) and session management. We do not use third-party tracking or advertising cookies.
            </p>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. We will notify registered users of any material changes via email or in-app notification.
            </p>
          </section>

          <section>
            <h2 className="text-[#D4AF37] text-lg font-semibold mb-3">12. Contact Us</h2>
            <p>
              If you have any questions about this privacy policy or our data practices, please contact us:
            </p>
            <div className="mt-3 bg-[#121212] border border-white/5 rounded-lg p-4 space-y-1">
              <p className="text-white/90 font-medium">2nd Team Casting</p>
              <p>Unit 121927, PO Box 6945, London, W1A 6US</p>
              <p>Email: <a href="mailto:info@2ndteam.co.uk" className="text-[#D4AF37] hover:underline">info@2ndteam.co.uk</a></p>
            </div>
            <p className="mt-3">
              You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">ico.org.uk</a> if you believe your data rights have been breached.
            </p>
          </section>

        </div>
      </div>

      <footer className="py-10 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-white/30 text-sm">© 2026 2nd Team Casting. All rights reserved.</div>
          <div className="flex items-center gap-6 text-white/40 text-sm">
            <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
